import React, { useEffect, useState } from "react";
import DynamicTable from "../TabComponents/DynamicTable";
import { useDispatch, useSelector } from "react-redux";
import { fetchDynamicData, processAdmission } from "../../../../../utils/Apis";
import { getValueByPath } from "../../../../../utils/getValues";
import {
  selectTabData,
  selectTabLoading,
} from "../../../../../features/dynamicSlice";
import AddIcon from "@mui/icons-material/Add";
import ProcessedAdmissionModal from "../TabComponents/ProcessedAdmissionModal";
import MessageModal from "../TabComponents/MessageModal";
import { Button, Chip } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { Snackbar, Alert } from "@mui/material";

const columns = [
  { label: "User ID", key: "visitorId", sortable: true },
  { label: "First Name", key: "firstName", sortable: true },
  { label: "Last Name", key: "lastName", sortable: true },
  {
    label: "Phone",
    key: "mobileNo",
    sortable: true,
  },
  {
    label: "Branch",
    key: "branchId.name",
    render: (row) => row.branchId?.name || "N/A",
    sortable: true,
  },
  {
    label: "Course",
    key: "courses[0].name",
    render: (row) => (
      <div style={{ display: "flex", overflowX: "auto", padding: "10px 0" }}>
        {row.courseId?.map((course) => (
          <Chip
            key={course.courseId}
            label={course.name || "N/A"}
            style={{ marginRight: "10px" }}
          />
        ))}
      </div>
    ),
    sortable: true,
  },
  { label: "Projected Fee", key: "feeStructure.finalFee", sortable: true },
  {
    label: "Status",
    key: "userStatus",
    sortable: true,
  },
];

const RequestsTab = () => {
  const dispatch = useDispatch();
  const requests = useSelector((state) => selectTabData(state, "requests"));
  const loading = useSelector((state) => selectTabLoading(state, "requests"));
  const token = useSelector((state) => state.auth.token);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [fields, setFields] = useState([
    { name: "email", label: "Username / Email", defaultValue: "N/A" },
    {
      name: "password",
      label: "Password",
      type: "password",
      defaultValue: "N/A",
    },
    {
      name: "message",
      label: "Message",
      multiline: true,
      rows: 4,
      fullWidth: true,
      defaultValue: "N/A",
    },
  ]);

  const [messageModalData, setMessageModalData] = useState(null);

  const handleOpenModal = (rowData) => {
    setSelectedAdmission(rowData);
    setOpenModal(true);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAdmission(null);
  };

  // General notification & modal message structure
  const showNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const showModal = (fields) => {
    setMessageModalData(fields);
    setMessageModalOpen(true);
  };

  const handleSubmitStudent = async (formData) => {
    setIsSubmitting(true); // Submit start hote hi loading true karo
    try {
      const resultAction = await dispatch(processAdmission(formData)).unwrap();

      const { status, data } = resultAction || {};

      if (status === 200) {
        showNotification(
          data?.message || "Admission processed successfully",
          "success"
        );

        showModal({
          message: data?.message || "Process completed successfully!",
        });
      } else if (status === 201) {
        showNotification(
          data?.message || "User created successfully",
          "success"
        );

        showModal({
          status: 201,
          email: data?.email || "N/A",
          password: data?.password || "N/A",
          userId: data?.userId || "N/A",
          message: data?.message || "User Created Successfully!",
        });
      } else if (status === 409) {
        showNotification(data?.message || "User already exists", "warning");

        showModal({
          message:
            data?.message || "Conflict occurred while processing the admission",
        });
      } else {
        showNotification("Unexpected status code returned", "info");

        showModal({
          message: "Unexpected response. Please contact support.",
        });
      }
    } catch (error) {
      console.error("Admission processing error:", error);

      showNotification(
        error?.data?.message || "Error while processing the admission",
        "error"
      );

      handleCloseModal();

      showModal({
        message:
          error?.data?.message ||
          "An unexpected error occurred while processing the admission.",
      });
    } finally {
      setIsSubmitting(false); // Request complete hone ke baad loading false karo
    }
  };

  const { id } = jwtDecode(token); // decoded payload (like userId, role, exp, etc.)

  const fetchUsers = async () => {
    try {
      const resultAction = await dispatch(
        fetchDynamicData({
          url: "/admissions",
          method: "POST",
          tab: "requests",
          body: {},
          params: {},
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch data once
  useEffect(() => {
    if (!requests || requests.length === 0) fetchUsers();
  }, []);

  // Filtered, searched, and sorted data
  const processedData = requests
    .filter((request) => {
      const status = request.requestDetails?.status?.toLowerCase();
      return statusFilter ? status === statusFilter.toLowerCase() : true;
    })
    .filter((request) => {
      const fullText =
        `${request.firstName} ${request.lastName} ${request.UserId} ${request.branchId?.name} ${request.requestDetails?.courses?.[0]?.name}`.toLowerCase();

      return fullText.includes(search.toLowerCase());
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      const valA = getValueByPath(a, sortField);
      const valB = getValueByPath(b, sortField);

      if (valA === undefined || valB === undefined) return 0;

      return sortOrder === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });

  const total = processedData.length;
  const start = (page - 1) * perPage;
  const paginatedData = processedData.slice(start, start + perPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="space-y-4">
      <div className=" flex items-center justify-between">
        <h1 className="text-xl font-bold">Requests List</h1>
      </div>

      {/* Filter + Search */}
      <div className="flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Search..."
          className="border rounded px-2 py-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="border rounded px-2 py-1"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="dropped">Dropped</option>
        </select>
      </div>

      <DynamicTable
        columns={columns}
        rows={paginatedData}
        total={total}
        page={page}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
        onRowClick={handleOpenModal}
      />
      <ProcessedAdmissionModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitStudent}
        adminId={id}
        issubmitting={isSubmitting}
        selectedAdmission={selectedAdmission}
      />

      <MessageModal
        open={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        title="Admission Process Status"
        fields={
          messageModalData?.status === 201
            ? [
                {
                  name: "userId",
                  label: "User ID",
                  defaultValue: messageModalData.userId,
                },
                {
                  name: "email",
                  label: "Email",
                  defaultValue: messageModalData.email,
                },
                {
                  name: "password",
                  label: "Password",
                  type: "text",
                  defaultValue: messageModalData.password,
                },
                {
                  name: "message",
                  label: "Message",
                  multiline: true,
                  rows: 6,
                  defaultValue: `${messageModalData.message} Kindly note these credentials for future, It'll not recoverable in future.`,
                },
              ]
            : [
                {
                  name: "message",
                  label: "Message",
                  multiline: true,
                  rows: 6,
                  defaultValue: messageModalData?.message,
                },
              ]
        }
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RequestsTab;
